import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { PageShell } from "../components/layout/PageShell";
import { DataTable, ViewModeToggle } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { FileText, PlayCircle, Search, ShieldCheck } from "lucide-react";
import { StatusBadge } from "../components/StatusBadge";
import {
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
  statusAccent,
} from "../components/EntityCardGrid";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import {
  listAssessmentPlans,
  type ApiAssessmentPlan,
  type AssessmentPlanStatus,
} from "../../api/frameworksApi";

export function AssessmentExecutionList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const [plans, setPlans] = useState<ApiAssessmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const plansRes = await listAssessmentPlans();
      const allPlans = plansRes?.data ?? [];
      const approved = allPlans.filter((p) => {
        const status = p.status ?? (p.lifecycleStatus as AssessmentPlanStatus);
        // Hide plans whose POA&M has already been generated — the backend
        // stamps `reviewCompletedAt` at that point, marking them finished.
        return status === "approved" && !p.reviewCompletedAt;
      });
      setPlans(approved);
    } catch (err) {
      console.error("Failed to load plans:", err);
      setError("Failed to load assessment plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const displayTitle = (p: ApiAssessmentPlan) =>
    p.title || p.documentId || p.id;

  const resolveSspTitle = (p: ApiAssessmentPlan): string | null => {
    if (p.sspTitle) return p.sspTitle;
    if (!p.importSspHref) return null;
    return p.importSspHref.split("/").pop() ?? p.importSspHref;
  };

  const filtered = plans.filter((p) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      displayTitle(p).toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
  });

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    let aVal: any = "";
    let bVal: any = "";
    if (sortKey === "name") {
      aVal = displayTitle(a);
      bVal = displayTitle(b);
    } else if (sortKey === "ssp") {
      aVal = resolveSspTitle(a) ?? "";
      bVal = resolveSspTitle(b) ?? "";
    } else if (sortKey === "controls") {
      aVal = a.controlCount ?? a.reviewedControls?.length ?? 0;
      bVal = b.controlCount ?? b.reviewedControls?.length ?? 0;
    } else if (sortKey === "startDate") {
      aVal = a.startDate ? new Date(a.startDate).getTime() : 0;
      bVal = b.startDate ? new Date(b.startDate).getTime() : 0;
    } else if (sortKey === "endDate") {
      aVal = a.endDate ? new Date(a.endDate).getTime() : 0;
      bVal = b.endDate ? new Date(b.endDate).getTime() : 0;
    }
    if (typeof aVal === "number")
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    return sortDir === "asc"
      ? aVal.localeCompare(bVal as string)
      : (bVal as string).localeCompare(aVal);
  });

  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <PageShell className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>Assessment Execution</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            Execute approved assessment plans and record control findings
          </p>
        </div>
      </div>

      {/* Search + Rows per page */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
          <input
            type="text"
            placeholder="Search plans"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500/50 transition-all placeholder:text-[#64748b] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
          />
        </div>
        <div className="flex items-center gap-3 ml-auto flex-shrink-0">
          <ViewModeToggle
            value={viewMode}
            onChange={(mode) => {
              setViewMode(mode);
              setPageSize(resolvePageSizeForViewMode(pageSize, mode));
              setPage(0);
            }}
          />
          <label className="text-sm d whitespace-nowrap">Rows per page</label>
          <Dropdown
            value={String(pageSize)}
            onChange={(v) => {
              setPageSize(Number(v));
              setPage(0);
            }}
            options={getPageSizeOptions(viewMode).map((n) => ({
              value: String(n),
              label: String(n),
            }))}
            size="sm"
            align="end"
            className="!min-w-[80px]"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable<ApiAssessmentPlan>
        columns={[
          {
            key: "no",
            header: "#",
            render: (_row, idx) => (
              <span className="text-[#0f172a] dark:text-white/80 font-medium">
                {page * pageSize + idx + 1}
              </span>
            ),
          },
          {
            key: "name",
            align: "left",
            ellipsis: 2,
            tooltip: (p) => displayTitle(p) || "",
            header: (
              <button
                onClick={() => handleSort("name")}
                className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Plan Name
                <SortIcon col="name" />
              </button>
            ),
            render: (p) => (
              <p className="font-medium text-[#0f172a] dark:text-white/80">
                {displayTitle(p)}
              </p>
            ),
          },
          {
            key: "ssp",
            align: "left",
            ellipsis: 2,
            tooltip: (p) => resolveSspTitle(p) || "",
            header: (
              <button
                onClick={() => handleSort("ssp")}
                className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Linked SSP
                <SortIcon col="ssp" />
              </button>
            ),
            render: (p) =>
              resolveSspTitle(p) ? (
                <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                  {resolveSspTitle(p)}
                </span>
              ) : (
                <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                  -
                </span>
              ),
          },
          {
            key: "controls",
            align: "center",
            header: (
              <button
                onClick={() => handleSort("controls")}
                className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Controls
                <SortIcon col="controls" />
              </button>
            ),
            render: (p) => (
              <span className="font-medium text-[#0f172a] dark:text-white/80">
                {p.controlCount ?? p.reviewedControls?.length ?? 0} controls
              </span>
            ),
          },
          {
            key: "startDate",
            align: "center",
            header: (
              <button
                onClick={() => handleSort("startDate")}
                className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                Start Date
                <SortIcon col="startDate" />
              </button>
            ),
            render: (p) => (
              <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                {p.startDate
                  ? new Date(p.startDate).toLocaleDateString("en-GB")
                  : "-"}
              </span>
            ),
          },
          {
            key: "endDate",
            align: "center",
            header: (
              <button
                onClick={() => handleSort("endDate")}
                className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                End Date
                <SortIcon col="endDate" />
              </button>
            ),
            render: (p) => (
              <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                {p.endDate
                  ? new Date(p.endDate).toLocaleDateString("en-GB")
                  : "-"}
              </span>
            ),
          },
        ]}
        data={paged}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyText={
          search
            ? "No plans match your search"
            : "No approved plans available for execution"
        }
        onRowClick={(p) => navigate(`/assessment-execution/${p.id}`)}
        rowKey={(p) => p.id}
        page={page}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={setPage}
        itemLabel="plans"
        enableCardView
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        renderCard={(rows) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
            {rows.map((p) => {
              const lc = (p.status ?? p.lifecycleStatus ?? "approved") as Parameters<
                typeof StatusBadge
              >[0]["status"];
              const accent = statusAccent(lc);
              const title = displayTitle(p);
              const sspTitle = resolveSspTitle(p);
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/assessment-execution/${p.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/assessment-execution/${p.id}`);
                    }
                  }}
                  className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
                  />
                  <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex-1 min-w-0">
                        <TitleTooltip text={title}>
                          <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                            {title}
                          </h3>
                        </TitleTooltip>
                      </div>
                      <div className="shrink-0">
                        <PlayCircle className="size-4 text-indigo-500 dark:text-indigo-400" />
                      </div>
                    </div>
                    {sspTitle && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 whitespace-nowrap max-w-full truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
                          <FileText className="size-3 flex-shrink-0" />
                          <span className="truncate">{sspTitle}</span>
                        </span>
                      </div>
                    )}
                    <div className="mt-auto flex flex-col gap-3">
                      <StatTiles>
                        <StatTile
                          icon={<ShieldCheck className="size-3.5" />}
                          accent="indigo"
                          value={p.controlCount ?? 0}
                          label="Controls"
                        />
                      </StatTiles>
                      <MetaDates
                        createdLabel="Start"
                        updatedLabel="End"
                        created={safeFormatDate(p.startDate)}
                        updated={safeFormatDate(p.endDate)}
                      />
                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#eef2f6] dark:border-white/[0.06]">
                        <StatusBadge status={lc} dot />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      />
    </PageShell>
  );
}
